import { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainButton } from '@telegram-apps/sdk-react';
import { motion } from 'framer-motion';
import { getTrackBackground, Range } from 'react-range';

import {
  containerStyle,
  heightTextStyle,
  inputContainerStyle,
  rangeContainerStyle,
  resetButtonContainerStyle,
  resetButtonStyle,
  thumbStyle,
  trackStyle
} from '@/pages/Profile/Edit/EditHeight/styles.ts';

import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import RulerShape from '@/components/common/svg/shapes/RulerShape.tsx';

const MIN_HEIGHT = 110;
const MAX_HEIGHT = 230;

interface EditHeightPopupProps {
  handleClose: () => void;
}

const EditHeightPopup: FC<EditHeightPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);
  
  const { aboutMe, updateProfile } = useProfileStore();
  const { t } = useTranslation();

  const defaultHeight = aboutMe?.profile.height_cm ?? 0;
  const [height, setHeight] = useState<number>(defaultHeight);
  const [tempHeight, setTempHeight] = useState<number>(
    defaultHeight === 0 ? MIN_HEIGHT : defaultHeight
  );
  const [isEdited, setIsEdited] = useState<boolean>(false);

  const handleChange = useCallback((newValues: number[]) => {
    const [newTempHeight] = newValues;
    setTempHeight(newTempHeight);
  }, []);

  const handleFinalChange = useCallback(
    (newValues: number[]) => {
      const [newHeight] = newValues;
      if (newHeight !== height) {
        setHeight(newHeight);
        setIsEdited(true);
      }
      setTempHeight(newHeight);
    },
    [height]
  );

  const resetHeight = async () => {
    setHeight(0);
    setTempHeight(MIN_HEIGHT);
    setIsEdited(false);
    if (aboutMe?.profile.height_cm !== 0) {
      await updateProfile({ height_cm: 0 });
    }
  };

  const submitHeight = async () => {
    mainButton.setParams({
      isLoaderVisible: true,
      isVisible: false,
      isEnabled: false
    });

    try {
      await updateProfile({ height_cm: height });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({ isLoaderVisible: false, isEnabled: true });
      setIsEdited(false);
    }
  };

  const trackBackground = useMemo(() => {
    return getTrackBackground({
      values: [tempHeight],
      colors: ['#ccc', '#ccc'],
      min: MIN_HEIGHT,
      max: MAX_HEIGHT
    });
  }, [tempHeight]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });
    mainButton.onClick(submitHeight);

    return () => mainButton.offClick(submitHeight);
  }, [submitHeight]);

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <RulerShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_your_height')}</div>
      </div>

      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={heightTextStyle}>
              {tempHeight === MIN_HEIGHT && height === 0
                ? t('not_selected')
                : `${tempHeight} ${t('height_cm')}`}
            </div>

            <div style={rangeContainerStyle}>
              <Range
                step={1}
                min={MIN_HEIGHT}
                max={MAX_HEIGHT}
                values={[tempHeight]}
                onChange={handleChange}
                onFinalChange={handleFinalChange}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      ...trackStyle,
                      background: trackBackground
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    key={props.key}
                    style={{
                      ...props.style,
                      ...thumbStyle
                    }}
                  />
                )}
              />
            </div>

            {height !== 0 && (
              <div style={resetButtonContainerStyle}>
                <motion.button
                  style={resetButtonStyle}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetHeight}
                >
                  {t('delete_height')}
                </motion.button>
              </div>
            )}
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditHeightPopup;
